import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, Info, CalendarClock, Trash2 } from "lucide-react";
import { toast } from "sonner";

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}
const STATUS_META = {
  aberto: { label: "Aberto", cls: "bg-green-100 text-green-800" },
  encerrado: { label: "Encerrado", cls: "bg-red-100 text-red-800" },
  nao_iniciado: { label: "Ainda não iniciado", cls: "bg-yellow-100 text-yellow-800" },
  desativado: { label: "Desativado", cls: "bg-slate-200 text-slate-700" },
};
function statusBadge(status) {
  if (!status) return null;
  const key = status.aberto ? "aberto" : status.motivo || "desativado";
  const meta = STATUS_META[key] || STATUS_META.desativado;
  return <Badge className={meta.cls} data-testid="atualizacao-status-badge">{meta.label}</Badge>;
}

export default function ConfigAtualizacaoPage() {
  const [allSections, setAllSections] = useState([]);
  const [permitidas, setPermitidas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Período
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [status, setStatus] = useState(null);
  const [savingPeriodo, setSavingPeriodo] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [secR, perR] = await Promise.all([
        api.get("/config/updateable-sections"),
        api.get("/config/periodo-atualizacao"),
      ]);
      setAllSections(secR.data.all_sections);
      setPermitidas(secR.data.sections);
      setInicio(isoToLocalInput(perR.data.inicio));
      setFim(isoToLocalInput(perR.data.fim));
      setAtivo(perR.data.ativo !== false);
      setStatus(perR.data.status);
    } catch (e) {
      logger.error("Falha ao carregar config", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggle = (sec) => {
    setPermitidas((p) => p.includes(sec) ? p.filter((x) => x !== sec) : [...p, sec]);
  };

  const salvarSecoes = async () => {
    setSaving(true);
    try {
      await api.put("/config/updateable-sections", { sections: permitidas });
      toast.success("Seções salvas!");
    } catch (e) {
      logger.error("Falha ao salvar config", e);
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const salvarPeriodo = async () => {
    if (inicio && fim && new Date(inicio) >= new Date(fim)) {
      toast.error("A data de início deve ser anterior à data de fim");
      return;
    }
    setSavingPeriodo(true);
    try {
      const r = await api.put("/config/periodo-atualizacao", {
        inicio: localInputToIso(inicio),
        fim: localInputToIso(fim),
        ativo,
      });
      setStatus(r.data.status);
      toast.success("Período salvo!");
    } catch (e) {
      logger.error("Falha ao salvar período", e);
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSavingPeriodo(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="config-atualizacao-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6]">Atualização Cadastral Permitida</h1>
        <p className="text-sm text-slate-600">
          Controle o período e as seções que o cidadão pode atualizar pela consulta pública (via senha temporária enviada por e-mail).
        </p>
      </div>

      {/* Período de Atualização */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="w-4 h-4" />Período de Atualização Cadastral
            </CardTitle>
            <CardDescription>Data de início e fim em que o cidadão poderá solicitar senha de atualização.</CardDescription>
          </div>
          {statusBadge(status)}
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-[#083CA6]/30 bg-[#083CA6]/5">
            <Info className="w-4 h-4 text-[#083CA6]" />
            <AlertDescription className="text-sm">
              Fora do período configurado, o cidadão receberá uma mensagem informando quando poderá atualizar seus dados.
              Deixe as datas em branco para permitir sem prazo.
            </AlertDescription>
          </Alert>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded border">
            <div>
              <Label className="font-semibold">Atualização Ativa</Label>
              <p className="text-xs text-slate-500">Suspende sem apagar as datas.</p>
            </div>
            <Switch checked={ativo} onCheckedChange={setAtivo} data-testid="switch-atualizacao-ativo" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="atu-inicio">Início</Label>
              <Input id="atu-inicio" type="datetime-local" value={inicio}
                     onChange={(e) => setInicio(e.target.value)} data-testid="atualizacao-inicio" />
            </div>
            <div>
              <Label htmlFor="atu-fim">Fim</Label>
              <Input id="atu-fim" type="datetime-local" value={fim}
                     onChange={(e) => setFim(e.target.value)} data-testid="atualizacao-fim" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => { setInicio(""); setFim(""); }} data-testid="btn-limpar-atualizacao-datas">
              <Trash2 className="w-4 h-4 mr-2" /> Limpar datas
            </Button>
            <Button onClick={salvarPeriodo} disabled={savingPeriodo}
                    className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-salvar-atualizacao-periodo">
              <Save className="w-4 h-4 mr-2" />{savingPeriodo ? "Salvando..." : "Salvar Período"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Seções permitidas */}
      <Alert className="border-[#083CA6]/30 bg-[#083CA6]/5">
        <Info className="w-4 h-4 text-[#083CA6]" />
        <AlertDescription className="text-sm">
          Marque as seções liberadas. Campos como CPF, protocolo, status e histórico nunca são editáveis pelo cidadão.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Seções</CardTitle>
          <CardDescription>{permitidas.length} de {allSections.length} liberadas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {allSections.map((sec) => (
              <label key={sec} className="flex items-center gap-2 p-3 rounded border border-slate-200 hover:border-[#083CA6] cursor-pointer transition-colors">
                <Checkbox
                  checked={permitidas.includes(sec)}
                  onCheckedChange={() => toggle(sec)}
                  data-testid={`chk-sec-${sec}`}
                />
                <Label className="cursor-pointer">{sec}</Label>
              </label>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={salvarSecoes} disabled={saving}
                    className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-salvar-config">
              <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar Seções"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
